import React from "react";
import { useI18nContext } from "@procore/core-react";
import { AlertContext } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import { useToastAlertContext } from "@procore/toast-alert";
import { reportError } from "@/utils/logging-utils";
import type { NotifiableError } from "@bugsnag/js";
import type { EditAlertData } from "@/react/components/communications/communications-prop-types";
import { updateScheduledOrDraftAlert } from "./alert-queries";
import {
   AssignmentAlertsForm,
   type AssignmentAlertsFormSubmission,
} from "./assignment-alerts-form";

function getAlertSuccessToastKey(context: AlertContext): string {
   switch (context) {
      case AlertContext.DRAFTS:
         return "views.company.workforce_planning.communications.alert_toasts.draft_success";
      case AlertContext.SCHEDULED:
         return "views.company.workforce_planning.communications.alert_toasts.scheduled_success";
      default:
         return "views.company.workforce_planning.communications.alert_toasts.sent_success";
   }
}

export type EditAssignmentAlertTearsheetContentProps = {
   editAlertData: EditAlertData;
   onClose: () => void;
   onSaved?: () => void;
};

/**
 * Edit workflow: PATCHes an existing scheduled/draft alert (send now, reschedule, or keep as
 * draft). Owns only the update API call; the shared `AssignmentAlertsForm` provides the UI,
 * seeded from the alert being edited.
 */
export const EditAssignmentAlertTearsheetContent = ({
   editAlertData,
   onClose,
   onSaved,
}: EditAssignmentAlertTearsheetContentProps) => {
   const I18n = useI18nContext();
   const { showToast } = useToastAlertContext();

   async function handleSave({
      context,
      values,
      alertContent,
      allRecipients,
   }: AssignmentAlertsFormSubmission) {
      try {
         await updateScheduledOrDraftAlert(
            editAlertData.alertId,
            editAlertData.projectId,
            {
               recipients: allRecipients,
               subject: alertContent.subject,
               content: alertContent.content,
               scheduled_day: values.scheduled_day,
               hour: values.hour,
               minute: values.minute,
               day_night: values.day_night,
            },
            context,
         );
         showToast.success(I18n.t(getAlertSuccessToastKey(context)));
         onSaved?.();
         onClose();
      } catch (error) {
         reportError(
            error as NotifiableError,
            "edit-assignment-alert-tearsheet-content__save-edit",
         );
         showToast.error(
            I18n.t(
               context === AlertContext.DRAFTS
                  ? "views.company.workforce_planning.communications.alert_toasts.draft_failure"
                  : "views.company.workforce_planning.communications.alert_toasts.failure",
            ),
         );
      }
   }

   return (
      <AssignmentAlertsForm
         projectId={editAlertData.projectId}
         initialContent={{
            subject: editAlertData.initialValues.subject ?? "",
            content: editAlertData.initialValues.content ?? "",
         }}
         initialRecipients={editAlertData.initialValues.recipients ?? []}
         initialSchedule={{
            scheduled_day: editAlertData.initialValues.scheduled_day,
            hour: editAlertData.initialValues.hour,
            minute: editAlertData.initialValues.minute,
            day_night: editAlertData.initialValues.day_night,
         }}
         scheduledByDefault={editAlertData.initialContext === AlertContext.SCHEDULED}
         onClose={onClose}
         onSave={handleSave}
      />
   );
};
